import { createContext, useContext, useEffect, useMemo, useState } from "react"
import type { ReactNode } from "react"

export type Theme = "light" | "dark"

const STORAGE_KEY = "dropx.admin.theme"

type ThemeContextValue = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readInitialTheme(): Theme {
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === "light" || stored === "dark") return stored
  /*
   * Dark unless the OS explicitly asks for light. The brand makes dark the
   * primary environment for operations, tracking and dispatch, so an operator on
   * a default OS still lands on the Obsidian canvas. This mirrors the inline
   * script in `index.html` exactly — the two must agree or the app repaints once
   * on mount.
   */
  return !window.matchMedia("(prefers-color-scheme: light)").matches ? "dark" : "light"
}

/**
 * The admin owns the `dark` class.
 *
 * `@dropx/ui` ships the tokens for both schemes but no mechanism to switch, and
 * its `Toaster` needs the resolved theme handed to it — so one class on `<html>`
 * plus this context is the whole contract.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readInitialTheme)

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark")
    document.documentElement.style.colorScheme = theme
    window.localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  const value = useMemo(() => ({ theme, setTheme }), [theme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) throw new Error("useTheme must be used inside <ThemeProvider>")
  return context
}
